import { useEffect, useRef, useState } from "react";
import clsx from "clsx";

import classes from "./styles.module.scss";

import type { IAudioPlayerProps } from ".";

import { Icon } from "@components/atoms/icons";
import { Typography } from "@components/atoms/typography";

function getTime(t: number) {
  return `${Number.parseInt((t / 60 / 10).toString())}${Number.parseInt(
    ((t / 60) % 10).toString(),
  )}:${Number.parseInt(((t % 60) / 10).toString())}${Number.parseInt(
    ((t % 60) % 10).toString(),
  )}`;
}

export function CustomAudioPlayer({ src }: IAudioPlayerProps) {
  const playerRef = useRef<HTMLButtonElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const timeLineRef = useRef<HTMLInputElement>(null);
  const soundRef = useRef<HTMLButtonElement>(null);

  const [mute, setMute] = useState(false);
  const [time, setTime] = useState("00:00");
  const [end, setEnd] = useState(false);
  const [play, setPlay] = useState(false);
  const [duration, setDuration] = useState("00:00");

  const setAudioTime = () => {
    if (audioRef.current && audioRef.current.duration > 0) {
      const t = audioRef.current.duration;

      setDuration(getTime(t));
    }
  };

  useEffect(() => {
    setAudioTime();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioRef.current]);

  return (
    <div className={clsx(classes.audioContainer)}>
      <div className={clsx(classes.audioPlayer)}>
        <audio
          onEnded={() => {
            setEnd(true);
          }}
          onTimeUpdate={() => {
            if (audioRef.current && timeLineRef.current) {
              const percentagePosition =
                (100 * audioRef.current.currentTime) /
                audioRef.current.duration;

              timeLineRef.current.style.backgroundSize = `${percentagePosition}% 100%`;
              timeLineRef.current.value = percentagePosition.toString();
              const t = audioRef.current.currentTime;

              setTime(getTime(t));
            }
          }}
          preload={"metadata"}
          onLoadedMetadata={() => {
            setAudioTime();
          }}
          onDurationChange={() => {
            setAudioTime();
          }}
          ref={audioRef}
          src={src}
        />
        <div className={clsx(classes.audioPlayer__controls)}>
          <button
            type={"button"}
            className={clsx(classes.audioPlayer__btn)}
            ref={playerRef}
            onClick={() => {
              if (end) {
                setEnd(false);
              }
              if (audioRef.current && playerRef.current) {
                if (audioRef.current.paused) {
                  audioRef.current.play();
                  setPlay(true);
                } else {
                  audioRef.current.pause();
                  setPlay(false);
                }
              }
            }}
          >
            {play && !end ? (
              <Icon
                name={"Pause"}
                type={"bold"}
                fontSize={"2xl"}
                color={"primary-text"}
              />
            ) : (
              <Icon
                name={"Play"}
                type={"bold"}
                fontSize={"2xl"}
                color={"primary-text"}
              />
            )}
          </button>
          <div
            className={clsx(
              classes.audioPlayer__texts,
              classes.audioPlayer__shrink,
            )}
          >
            <Typography variant={"subtitle3"}>
              {time}
              {" / "}
              {duration || "0"}
            </Typography>
          </div>
          <input
            type={"range"}
            className={clsx(classes.audioPlayer__timeline)}
            max={"100"}
            value={"0"}
            ref={timeLineRef}
            onChange={() => {
              if (timeLineRef.current && audioRef.current) {
                audioRef.current.currentTime =
                  (Number(timeLineRef.current.value) *
                    audioRef.current.duration) /
                  100;
              }
            }}
          />
          <button
            type={"button"}
            className={clsx(
              classes.audioPlayer__btn,
              classes.audioPlayer__shrink,
            )}
            ref={soundRef}
            onClick={() => {
              if (audioRef.current) {
                audioRef.current.muted = !audioRef.current.muted;
              }
              setMute((prevValue) => !prevValue);
            }}
          >
            {mute ? (
              <Icon
                name={"Muted"}
                type={"bold"}
                fontSize={"2xl"}
                color={"primary-text"}
              />
            ) : (
              <Icon
                name={"VolumeLoud"}
                type={"bold"}
                fontSize={"2xl"}
                color={"primary-text"}
              />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
